import React from 'react'
import { Link } from 'react-router-dom'
import { Table, Tag, Popover, Popconfirm, Space, Button, message } from 'antd'
import { EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { ListComponent, BaseListComponentProps } from '../BaseComponent'
import { SearchRequest, ShelterTaxDeleteRequest } from '../../services/api/interfaces/ApiRequest'
import IShelterTax from '../../interfaces/IShelterTax'

type ShelterTaxListProps = BaseListComponentProps & {
  shelterUuid: string,
  loading: boolean,
  taxes: IShelterTax[] | null,
  pagination: any,

  history: any,

  loadTaxes: (args: SearchRequest) => void,
  deleteTax: (args: ShelterTaxDeleteRequest) => Promise<any>
}

export default class ShelterTaxList extends ListComponent<ShelterTaxListProps> {

  static defaultProps = {
    loading: false,
  }

  // Table for branches popover
  branchesTableColumns = [
    {
      title: 'Name',
      dataIndex: ['branch', 'name'],
      key: 'name'
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (text: string, record: any) => (
        <Tag color="green">{Number(text).toFixed(2)}</Tag>
      )
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      render: (text: string, record: any) => (
        <Tag color={record.active ? 'cyan' : 'purple'}>{record.active ? 'Yes' : 'No'}</Tag>
      )
    },
    {
      title: 'Required',
      dataIndex: 'required',
      key: 'required',
      render: (text: string, record: any) => (
        <Tag color={record.required ? 'cyan' : 'purple'}>{record.required ? 'Yes' : 'No'}</Tag>
      )
    }
  ]

  tableColumns = [
    {
      title: 'UUID',
      dataIndex: 'uuid',
      key: 'uuid',
      responsive: ['md'] as any,
      sorter: (a: any, b: any) => a.uuid - b.uuid
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: any, b: any) => a.name - b.name
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      responsive: ['md'] as any,
      sorter: (a: any, b: any) => a.description - b.description
    },
    {
      title: 'Locations',
      dataIndex: 'tax_branch',
      key: 'tax_branch',
      responsive: ['md'] as any,
      render: (text: string, record: any) => {
        const dataSource = record.tax_branch || []

        const popoverContent = (
          <div style={{minHeight: 200, minWidth: 400, overflow: 'auto'}}>
            <Table dataSource={dataSource} columns={this.branchesTableColumns} />
          </div>
        )

        return (
          <Popover content={popoverContent} title="Locations" trigger="hover" placement="bottom">
            <Tag color="geekblue">{record.tax_branch?.length || 0}</Tag>
          </Popover>
        )
      }
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      responsive: ['md'] as any,
      sorter: (a: any, b: any) => a.price - b.price,
      render: (text: string, record: any) => (
        <Tag color="green">{Number(text).toFixed(2)}</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      render: (text: string, record: any) => (
        <Space size='middle'>
          <Link to={`/organization/shelter/${this.props.shelterUuid}/tax/${record.uuid}`} ref={(refs) => this.actionRefs.edit.push(refs)}>
            <Button type="dashed" shape="round" icon={<EditOutlined />} />
          </Link>

          <div ref={(refs) => this.actionRefs.delete.push(refs)}>
            <Popconfirm title="Are you sure about deleting this tax?" placement="topLeft" onConfirm={() => this.handleDelete(record.uuid)}>
              <Button type="dashed" shape="round" icon={<DeleteOutlined />} />
            </Popconfirm>
          </div>
        </Space>
      )
    }
  ]

  componentDidMount() {
    this.searchOptions.search = {
      shelter: {
        has: {
          uuid: this.props.shelterUuid
        }
      },
      'tax_branch': {
        with: {
          deleted: false,
          branch: {
            with: true
          }
        }
      }
    }

    this.props.loadTaxes(this.searchOptions)
  }

  handleDelete = async (uuid: string) => {
    const { deleteTax } = this.props

    await deleteTax({
      organization_tax_uuid: uuid
    })

    message.success('Tax deleted successfully!')
  }

  render() {
    const { loading, pagination, taxes, loadTaxes } = this.props

    let paginationOptions = {}
    if (pagination) {
      paginationOptions = {
        total: pagination.count,
        pageSize: pagination.limit,
        current: pagination.page
      }
    }

    return (
      <>
        <Table
          loading={loading}
          rowKey='uuid'
          columns={this.tableColumns}
          pagination={paginationOptions}
          dataSource={taxes as any}
          onChange={(pagination: any, filters: any, sorter: any) => this.handleTableChange(pagination, sorter, loadTaxes)}
        />
      </>
    )
  }
}
